import logoUrl from '@/assets/mainlogo.png'
import { cn } from '@/utils/cn'

export interface AuthHeaderProps {
  /**
   * Omitted on Login, where the brand mark alone heads the screen. The logo
   * then takes over as the accessible name, so the view is never heading-less.
   */
  readonly title?: string
  readonly description?: string
}

/**
 * The title block every unauthenticated screen opens with: the brand mark,
 * an optional bold heading and a muted supporting line.
 *
 * Shared rather than repeated because Reset Password alone renders it in three
 * different states, and all four auth views must stay identical.
 */
export function AuthHeader({ title, description }: AuthHeaderProps) {
  return (
    <div className="flex flex-col items-center text-center">
      <img
        src={logoUrl}
        // Decorative only while a heading follows — a real alt would announce
        // the brand on top of it. Without one, the logo is the accessible name.
        alt={title ? '' : 'Turon Travel'}
        width={384}
        height={384}
        // The full brand mark (emblem + wordmark). Height-constrained with an
        // auto width so the artwork keeps its ratio.
        className="h-24 w-auto object-contain"
      />
      {title && <h1 className="mt-5 text-2xl font-bold tracking-tight text-fg">{title}</h1>}
      {description && (
        // Sits tight under a heading, but takes the heading's own gap when it
        // follows the logo directly.
        <p className={cn('text-sm leading-relaxed text-fg-muted', title ? 'mt-1.5' : 'mt-5')}>
          {description}
        </p>
      )}
    </div>
  )
}
